/**
 * Format file size
 * @param bytes Number of bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const index = Math.min(i, sizes.length - 1);
  
  return parseFloat((bytes / Math.pow(k, index)).toFixed(2)) + ' ' + sizes[index];
}

/**
 * Format date string
 * @param dateString ISO date string
 * @returns Formatted date string (e.g., "2024/01/15 14:30")
 */
export function formatDate(dateString: string): string {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '-';
    }
    
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
}

/**
 * Get file icon by type
 * @param fileType MIME type or file extension
 * @returns Emoji icon for the file type
 */
export function getFileIcon(fileType: string): string {
  if (!fileType) return '📎';
  
  const type = fileType.toLowerCase();
  
  if (type.startsWith('image/')) return '🖼️';
  if (type.startsWith('video/')) return '🎬';
  if (type.includes('pdf')) return '📄';
  if (type.includes('word') || type.includes('document')) return '📝';
  if (type.includes('excel') || type.includes('sheet') || type.includes('csv')) return '📊';
  if (type.includes('powerpoint') || type.includes('presentation')) return '📽️';
  if (type.includes('zip') || type.includes('rar') || type.includes('7z')) return '📦';
  if (type.includes('audio/')) return '🎵';
  if (type.includes('text/')) return '📃';
  
  return '📎';
}

/**
 * Get file category name
 * @param category Category key
 * @returns Localized category name
 */
export function getCategoryName(category: string): string {
  const names: Record<string, string> = {
    all: '全部文件',
    image: '图片',
    video: '视频',
    document: '文档',
    other: '其他',
  };
  return names[category] || '其他';
}

/**
 * Check if file is an image
 * @param fileType MIME type
 * @returns Boolean
 */
export function isImage(fileType: string): boolean {
  if (!fileType) return false;
  return fileType.startsWith('image/');
}

/**
 * Check if file is a video
 * @param fileType MIME type
 * @returns Boolean
 */
export function isVideo(fileType: string): boolean {
  if (!fileType) return false;
  return fileType.startsWith('video/');
}

/**
 * Check if file type is allowed (not HTML)
 * @param fileName File name
 * @param fileType MIME type
 * @returns Boolean
 */
export function isFileTypeAllowed(fileName: string, fileType: string): boolean {
  if (!fileName) return false;
  
  const lowerFileName = fileName.toLowerCase();
  
  // Check file extension - only HTML files are forbidden
  if (lowerFileName.endsWith('.html') || lowerFileName.endsWith('.htm') || lowerFileName.endsWith('.xhtml')) {
    return false;
  }
  
  // Check MIME type if provided
  if (fileType) {
    const lowerFileType = fileType.toLowerCase();
    
    // Check MIME type
    const forbiddenTypes = [
      'text/html',
      'application/xhtml+xml',
      'application/xhtml',
    ];
    
    if (forbiddenTypes.some(type => lowerFileType.includes(type))) {
      return false;
    }
  }
  
  return true;
}

/**
 * Generate unique ID
 * @returns UUID-like string
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Calculate MD5 hash of a file (for deduplication)
 * Note: This requires spark-md5 to be loaded
 * @param file File object
 * @returns Promise<string>
 */
export async function calculateFileMD5(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const spark = new (window as any).SparkMD5.ArrayBuffer();
        spark.append(e.target?.result as ArrayBuffer);
        resolve(spark.end());
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Format duration in seconds to human readable string
 * @param seconds Duration in seconds
 * @returns Formatted string (e.g., "2:30")
 */
export function formatDuration(seconds: number): string {
  if (seconds < 0) return '0:00';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Truncate text with ellipsis
 * @param text Original text
 * @param maxLength Maximum length
 * @returns Truncated text
 */
export function truncateText(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

/**
 * Debounce function
 * @param fn Function to debounce
 * @param delay Delay in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Throttle function
 * @param fn Function to throttle
 * @param limit Limit in milliseconds
 * @returns Throttled function
 */
export function throttle<T extends (...args: any[]) => void>(
  fn: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Get file URL based on fileUrl and filePath
 * @param fileUrl Direct file URL (optional)
 * @param filePath R2 storage path (optional)
 * @returns File URL
 */
export function getFileUrl(fileUrl?: string, filePath?: string): string {
  // If fileUrl exists, use it directly
  if (fileUrl) {
    return fileUrl;
  }
  
  // If filePath exists, generate URL dynamically using VITE_FILES_PUBLIC_URL
  if (filePath) {
    const publicUrl = import.meta.env.VITE_FILES_PUBLIC_URL || '';
    // Remove trailing slash from publicUrl and leading slash from filePath
    const trimmedUrl = publicUrl.replace(/\/$/, '');
    const trimmedPath = filePath.replace(/^\//, '');
    return `${trimmedUrl}/${trimmedPath}`;
  }
  
  // Fallback: return empty string
  return '';
}
